import { useGetActiveGroupsQuery } from "@api/access-api"
import { useGetEventTypesQuery } from "@api/eventType-api"
import { useGetGroupByIdQuery, useSearchGroupsQuery } from "@api/group-api"
import { useGetRootGroupQuery, useSearchRootGroupsQuery } from "@api/rootGroup-api"
import { useGetRootGroupClassesQuery } from "@api/rootGroupClass-api"
import { useGetTaskDefinitionsQuery } from "@api/taskDefinition-api"
import { useGetTemplateRolesQuery } from "@api/templaterole-api"
import { userSelector } from "@redux/user"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { useListGeneralVideosQuery, useListRootGroupVideosQuery } from "../api/video-api"
import { locData } from "./localization"
import { isWithinCustomerGroup } from "./utils"

/**
 * Returns current profile's accessible groups, dependent on context.
 */
export const useGroups = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return []
	}

	return data.groups
}

/**
 * Returns current profile's accessible groups, dependent on context. Returns `useGetActiveGroupsQuery` status values unlike `useGroups`
 */
export const useGroupsWithStatus = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data, ...rest } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return {data: [], ...rest}
	}

	return {data: data.groups, ...rest}
}

/**
 * Returns current profile's accessible root groups, dependent on context.
 * This is primarily determined from being in `rootGroup.memberPool` array.
 */
export const useRootGroup = (opts = {}) => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return null
	}

	return data.rootGroup
}

/**
 * Returns current profile's accessible PATRONED root groups, dependent on context.
 * This is primarily determined from being in `rootGroup.patrons` array.
 * 
 * Supports admin context settings; will perform a query in that case.
 */
export const usePatronRootGroups = (opts = {}) => {
	const { contextData, profileDataForCtx } = useSelector(userSelector)
	const isPatron = contextData.groupRole === "patron"

	// Only run query when needed
	const dataCond = contextData.adminToggle && contextData.rootGroup && isPatron
	const { data } = useGetRootGroupQuery({ id: contextData.rootGroup }, { skip: !dataCond })

	const { removePassive } = opts

	if (!profileDataForCtx) {
		return []
	}

	if (!contextData.rootGroup)
		return []

	if (dataCond) {
		return data ? [data] : []
	} else {
		let res = profileDataForCtx?.patronRootGroups ?? []
		if (removePassive) {
			return res = res.filter(rg => !rg.passive)
		}
		return res
	}
}

/**
 * Returns current profile's specific group, dependent on context
 * 
 * Supports admin context settings; will perform a query in that case.
 */
export const useCurGroup = () => {
	const { contextData } = useSelector(userSelector)
	const groups = useGroups()
	return groups.find(g => (g?._id ?? g) === contextData.group)
}

export const useCurGroupMembers = () => {
	const group = useCurGroup()
	if (!group) {
		return []
	}
	return group.members
}

export const useAllAvailableMembers = () => {
	// Add non-handled query argument to refetch on context data changes
	const { contextData } = useSelector(userSelector)
	const { data } = useGetActiveGroupsQuery({ refetch: contextData })
	if (!data) {
		return []
	}
	const memberPoolMembers = !data.rootGroup ? [] : data.rootGroup.memberPool
	const rootGroupMembers = !data.rootGroup ? [] : data.rootGroup.members.map(m => m.profile)
	const groupMembers = data.groups.flatMap(g => g.members.map(m => m.profile))
	const members = _.uniqBy([...memberPoolMembers, ...rootGroupMembers, ...groupMembers], "name")

	return members
}

/**
 * Returns current profile's specific root group, dependent on context
 * 
 * Supports admin context settings; will perform a query in that case.
 */
export const useCurRootGroup = () => {
	const rootGroup = useRootGroup()
	return rootGroup
}

export const usePatronSubGroups = (opts = {}) => {
	const { contextData } = useSelector(userSelector)
	const rootGroup = useCurRootGroup()
	const { removePassive } = opts
	const isPatron = contextData.groupRole === "patron"
	if (!isPatron || !rootGroup) {
		return []
	}
	let res = rootGroup?.groups ?? []
	if (removePassive) {
		res = res.filter(g => !g.passive)
	}
	return res
}

/**
 * Returns the currently chosen role definition
 */
export const useRoleDefinition = () => {

	const { profileDataForCtx } = useSelector(userSelector)
	let roleDef = profileDataForCtx.activeMemberRole
	if (!roleDef) {
		return {
			rolePermMap: {},
			itemPerms: [],
			generalPerms: [],
			name: "basic_user",
			root: "",
			inGroups: [],
			roleProfileIdMap: {}
		}
	}

	return roleDef
}

/**
 * Returns the current operational class.
 * 
 * Falls back to the current sport context, and then the first found
 * operational root group class.
 * 
 * If null is returned from the hook, it means that queries
 * are loading. If the return value is undefined, it means that no operational
 * classes exist in the database.
 * 
 * @param {String} rgId													Optional parameter for retrieving a specific root group's operational class instead of from own context
 * @param {Object} opts													Options
 * @param {Boolean} opts.localize								Apply localization on RootGroupClass. Defaults to true.
 * @param {Boolean}	opts.ignoreDisplayNames			Prevent fallback to default language page customization display names if there is not one defined for current language. Defaults to true
 * @returns {Object}														RootGroupClass document of type "operational"
 */
export const useOperationalClass = (rgId, opts = {}) => {

	let { localize, ignoreDisplayNames } = opts
	if (localize === undefined) {
		localize = true
	}
	if (ignoreDisplayNames === undefined) {
		ignoreDisplayNames = true
	}

	rgId = rgId?._id ?? rgId

	const { contextData } = useSelector(userSelector)

	let rootGroupId = rgId ?? contextData.rootGroup
	const { data: rootData, isLoading: rootLoading } = useGetRootGroupQuery({ id: rootGroupId }, { skip: !rootGroupId })
	const { data: classData } = useGetRootGroupClassesQuery(null, { skip: rootLoading || rootData })

	if (!rootData && !classData) {
		return { eventTypes: [], personalEventTypes: [] }
	}

	let opClass

	if (rootData) {
		opClass = rootData?.classes?.find(c => c.type === "operational")
	}

	if (!opClass) {
		opClass = classData?.find(c => c.type === "operational" && c.name === "football")
	}

	if (localize) {
		opClass = locData(opClass, undefined, { ignoreDisplayNames })
	}

	return opClass
}

/**
 * Returns the current operational class.
 * 
 * Falls back to the current sport context, and then the first found
 * operational root group class.
 * 
 * If null is returned from the hook, it means that queries
 * are loading. If the return value is undefined, it means that no operational
 * classes exist in the database.
 * 
 * @param {String} rgId								Optional parameter for retrieving a specific root group's operational class instead of from own context
 * @returns {Object}									RootGroupClass document of type "operational"
 */
export const useProcessClasses = (rgId) => {

	rgId = rgId?._id ?? rgId

	const { contextData } = useSelector(userSelector)

	let rootGroupId = rgId ?? contextData.rootGroup
	const { data: rootData } = useGetRootGroupQuery({ id: rootGroupId }, { skip: !rootGroupId })

	if (!rootData) {
		return []
	}

	let processClasses

	if (rootData) {
		processClasses = rootData?.classes?.filter(c => c.type === "process")
	}

	return processClasses ?? []
}

/**
 * Returns available task definitions for the current root group context or first available.
 * Doed not include universal task types. If all task definitions are needed,
 * use a GET query.
 * 
 * `null` return value means that root group class query is loading.
 * `undefined` means that no operational root group class was found in query results.
 */
export const useTaskDefinitions = () => {
	const opClass = useOperationalClass()

	if (opClass) {
		return null
	}

	let availableTasks = opClass.eventTypes.reduce((a, b) => {
		const eventType = b
		let tasks = [...eventType.tasks]
		let accumulatedDefs = tasks
		for (let c of eventType.children) {
			let tasks = [...c.tasks]
			accumulatedDefs.push(...tasks)
		}
		return a.concat(accumulatedDefs)
	}, [])

	let result = _.uniqBy(availableTasks, "name")
	return result
}

/**
 * Returns a mapping of dot-separated event types to available
 * task definitions. Will propagate parent event types' tasks to children automatically.
 * 
 * Will return null if needed data is not yet available.
 */
export const useEventTaskMap = () => {
	const { data: eventTypes } = useGetEventTypesQuery()
	const { data: taskDefData } = useGetTaskDefinitionsQuery()
	if (!eventTypes || !taskDefData) {
		return null
	}
	let obj = eventTypes.reduce((a, b) => {
		const item = b
		let itemTasks = item.tasks ?? []
		itemTasks = itemTasks.map(id => taskDefData.find(d => d._id === id)).filter(o => o)
		_.set(a, item.name, itemTasks)
		for (let c of item.children) {
			let childTasks = c.tasks ?? []
			childTasks = childTasks.map(id => taskDefData.find(d => d._id === id)).filter(o => o)
			_.set(a, [`${item.name}.${c.name}`], _.uniqBy([...itemTasks, ...childTasks], "name"))
		}
		return a
	}, {})

	return obj
}


/**
 * Returns group options filtered by given rootgroups and customergroups array.
 * 
 * Useful for getting list of possible options as part of a filtering pipeline.
 * 
 * @param {{ rootGroups: object[], customerGroups: object[]}}
 */
export const useGroupOptionsForCustomerGroupsAndRootGroups = ({ customerGroups, rootGroups }) => {
	const { data: groups, isLoading } = useAllGroups()

	const getGroupOptions = (groups) => {
		return groups.map((group) => {
			if (
				rootGroups?.length > 0 &&
				!rootGroups?.some((r) => r?._id === group.root?._id)
			) {
				return undefined
			}

			if (customerGroups?.length > 0 && customerGroups.every((cg) => !isWithinCustomerGroup(group.root, cg))) {
				return undefined
			}

			return group
		})
	}

	return {
		isLoading,
		options: groups ? _.compact(getGroupOptions(groups)) : null
	}
}

/**
 * Handles processing of video data based on pagination options. Is also responsible for actually fetching the videolibrary videos based on the pagination options.
 * 
 * @param {{ isRootGroupView: boolean, customFilter: (video: object) => boolean }} 
 * 	Settings object. `isRootGroupView` determines what query to dispatch.
 * 	`customFilter` is an optional filter function applied after other filters.
 * 
 * @returns Object with important information such as whether query for videos is loading, 
 * the actual data response from the query, the pagination opts and finally the setter of the pagination options.
 */
export const useVideoPagination = (settings = { isRootGroupView: false, customFilter: undefined }) => {
	const { isRootGroupView } = settings
	const { contextData } = useSelector(userSelector)
	const [opts, setOpts] = useState({
		page: 1,
		limit: 20,
		search: "",
		categories: [],
		size: null,
		rootGroupId: null,
		year: null,
		month: null,
		weeks: null,
	})

	const query = isRootGroupView ? useListRootGroupVideosQuery : useListGeneralVideosQuery
	const { data: pagRes, isLoading, isFetching } = query({
		id: contextData.rootGroup,
		query: {
			paginate: true,
			...opts,
			month: opts.month !== null && !isNaN(opts.month) ? opts.month + 1 : null, // month is stored as zero-index
			excludeClips: true,
		}
	})

	/** Processes items by applying filtering  */
	const flattenVideos = () => {
		let data = _.cloneDeep(pagRes)

		if (pagRes?.docs) {
			let videos = _.cloneDeep(pagRes.docs)
			if (videos?.general && videos?.rootGroup) {
				const generalVids = pagRes?.docs?.general ?? []
				const rootGroupVids = pagRes?.docs?.rootGroup ?? []

				videos = generalVids.concat(rootGroupVids)
			}

			data.docs = videos
		}

		return data
	}

	return {
		data: flattenVideos(),
		isLoading,
		isFetching,
		opts,
		setOpts,
	}
}

/**
 * Returns all video categories user has access to. For admins, fetches all video categories across all operational classes. 
 * 
 * Ensures uniqueness.
 * @returns {string[]}
 */
export const useVideoCategories = () => {
	const { data: rootGroupClasses } = useGetRootGroupClassesQuery(null)
	const { contextData, isPlainAdmin } = useSelector(userSelector)
	const op = useOperationalClass(contextData.rootGroup)

	if (!op) return []

	return _(rootGroupClasses)
		.filter((o) => (isPlainAdmin || o.name === op.name) && o.type === "operational")
		.map((o) => o?.videoCategories ?? [])
		.flattenDeep()
		.uniq()
		.value()
}

/**
 * Returns template role options for admin. Useful for situations where admin can select multiple template roles.
 * @returns 
 */
export const useTemplateRoleOptionsAdmin = () => {
	const { data: templateRoles, isLoading } = useGetTemplateRolesQuery(null)

	const getTemplateRoleOptions = () => {
		return _(templateRoles)
			.uniqBy("_id")
			.flatten()
			.value()
	}

	return {
		isLoading,
		options: getTemplateRoleOptions()
	}
}

/**
 * Returns rootgroups options filtered by given customergroups array.
 * 
 * Useful for getting list of possible options as part of a filtering pipeline.
 * 
 * @param {number[]} customerGroups - One or many customer group objects in array
 */
export const useRootGroupOptionsForCustomerGroups = (customerGroups) => {
	const { data: rootGroupData, isLoading } = useSearchRootGroupsQuery({
		pagination: false,
		limited: 1,
	})

	const getRootGroupOptions = (rootGroups) => {
		return rootGroups.map((rG) => {
			if (customerGroups?.length > 0) {
				if (customerGroups.every((cg) => !isWithinCustomerGroup(rG, cg))) {
					return undefined
				}
			}

			return { _id: rG?._id, name: rG?.name }
		})
	}

	return {
		isLoading,
		options: rootGroupData ? _.compact(getRootGroupOptions(rootGroupData.docs)) : null
	}

}

/**
 * Returns all groups under rootgroups fetched via `useSearchRootGroupsQuery`.
 * 
 * Useful for admin-level functionality since effectively returns all possible groups
 * 
 * @returns 
 */
export const useAllGroups = () => {
	const { data: rootGroupData, isLoading: isRootGroupsLoading } = useSearchRootGroupsQuery({
		pagination: false,
		limited: 1,
	})

	return {
		isLoading: isRootGroupsLoading,
		data: rootGroupData?.docs?.flatMap((o) => o.groups.map((g) => ({ ...g, groupHeader: o.name, root: o })))
	}
}

/**
 * Returns list of members of current group, optionally filtered by role name
 * @param {String} role 					Role name
 * @returns {[Object]}						Array of `group.members` path objects
 */
export const useMembersInGroup = (role, groupId) => {
	const { contextData, isAdmin } = useSelector(userSelector)
	const curGroupId = groupId ?? contextData.group
	const curGroupMembers = useCurGroupMembers()

	const { data: groupData, isLoading } = useGetGroupByIdQuery(curGroupId, { skip: !isAdmin || !curGroupId })

	if (!curGroupId || isLoading) {
		return []
	}

	let members = []
	if (isAdmin) {
		const group = groupData
		members = group?.members ?? []
	} else {
		members = curGroupMembers ?? []
	}

	if (role) {
		members = members.filter(m => m.profile && m.hasGroupRoles.includes(role))
	}


	return members
}

export const useAvailableMembers = ({
	groupRole="player",
	selectedGroups=[],
	whitelist=[],
	blacklist=[]
}) => {
	const {data: currentGroups, isLoading, ...rest} = useGroupsWithStatus()

	if (isLoading) {
		return {
			availableMembers: [],
			isLoading,
			...rest
		}
	}

	const groupSubset = currentGroups.filter(g => (selectedGroups.length > 0 ? selectedGroups.some(sG => g._id === sG._id) : true))
	const subsetMembers = groupSubset.flatMap(g => g.members)

	let availableMembers = subsetMembers
		.filter(m => m.profile && m.hasGroupRoles.includes(groupRole))
		.map(m => ({ name: m.profile.name, id: m.profile._id }))
	if (whitelist.length > 0) {
		availableMembers = availableMembers.filter(o => whitelist.includes(o.id))
	}
	if (blacklist.length > 0) {
		availableMembers = availableMembers.filter(o => !blacklist.includes(o.id))
	}
	// Remove objects with undefined values; else causes error
	availableMembers = availableMembers.filter(a => !!a.name && !!a.id)
	// Sort by name
	availableMembers.sort((a,b) => a.name.localeCompare(b.name))
	
	return {
		availableMembers,
		isLoading,
		...rest
	}
}


/**
 * Return all groups where the user is a member or the patron of.
 * 
 * The group members will be populated to include 'profile.name' attribute.
 * 
 * @param {String} rootGroupId						Filters groups by root group. Mainly relevant for admin with no chosen root group.
 * @returns {[Object]}										Array of groups
 */
export const useActiveGroups = (rootGroupId) => {
	const { contextData } = useSelector(userSelector)
	const { data } = useSearchGroupsQuery({ rootGroupId: rootGroupId ?? contextData.rootGroup, populateMembers: 1, limited: 1 })

	return data?.docs ?? []
}

/**
 * Returns all members that can be derived from active groups, ie. groups that the
 * current user has access to based on context role or in a particular root group.
 * 
 * The members will be populated to include 'profile.name' attribute. The result will not include
 * profiles that cannot be derived from groups (patrons, root group members with zero groups).
 * 
 * @param {String} rootGroupId 		Filters source groups by root group. Mainly relevant for admin with no chosen root group.
 * @param {Boolean} mergeRoles 		If a member is in multiple groups, all cumulative roles will be included. Otherwise the first
 * 																found membership instance is used for the profile.
 * @returns {[Object]}						Array of `Group.members` items
 */
export const useActiveGroupsMembers = (rootGroupId, mergeRoles) => {
	const groups = useActiveGroups(rootGroupId)
	let members = _.flatMap(groups, "members")
	if (mergeRoles) {
		const withMergedRoles = _.values(_.groupBy(members, m => m.profile._id)).reduce((a, b) => {
			const arr = b
			if (arr.length === 1) {
				return a.concat(arr)
			} else {
				const hasGroupRoles = _.uniq(_.flatMap(arr, "hasGroupRoles"))
				return a.concat({ ...arr[0], hasGroupRoles })
			}
		}, [])
		members = withMergedRoles
	} else {
		members = _.uniqBy(members, m => m.profile._id)
	}
	members = members.filter(m => m.profile)
	members.sort((a, b) => a.profile.name.localeCompare(b.profile.name))
	return members
}

/**
 * Checks presence of permission using `Profile.groupRoleOverview` path data.
 * This is dependent on the current root group context and admin status.
 * 
 * @param {String} resource 						Permission resource
 * @param {String} action 							Permission action
 * @param {String} targetRole 					Role identifier in `rolePermMap`
 * @param {Boolean} allowPatron 				Whether Patron should automatically have permission
 * @returns {Boolean}										Whether permission is possessed
 */
export const useRolePerm = (resource, action, targetRole, allowPatron = true) => {
	const { profileDataForCtx, contextData, isAdmin } = useSelector(userSelector)
	const isPlainAdmin = isAdmin && !contextData.rootGroup
	if (!profileDataForCtx) {
		return false
	}

	const rootObj = _.get(profileDataForCtx, ["groupRoleOverview", contextData.rootGroup])
	const hasPatron = _.get(rootObj, "isPatron") === true
	const hasPerm = _.get(rootObj, "memberRoles")?.some(mr => {
		return _.get(mr, ["rolePermMap", targetRole])?.some(p => p.resource === resource && p.actions.includes(action))
	})
	return isPlainAdmin || (allowPatron && hasPatron) || hasPerm
}


/**
 * Checks presence of permission using `Profile.groupRoleOverview` path data.
 * This is dependent on the current root group context and admin status.
 * 
 * @param {String} resource 						Permission resource
 * @param {String} action 							Permission action
 * @param {Boolean} allowPatron 				Whether Patron should automatically have permission
 * @returns {Boolean}										Whether permission is possessed
 */
export const useItemPerm = (resource, action, allowPatron = true) => {
	const { profileDataForCtx, contextData, isAdmin } = useSelector(userSelector)
	const isPlainAdmin = isAdmin && !contextData.rootGroup
	if (!profileDataForCtx) {
		return false
	}

	const rootObj = _.get(profileDataForCtx, ["groupRoleOverview", contextData.rootGroup])
	const hasPatron = _.get(rootObj, "isPatron") === true
	const hasPerm = _.get(rootObj, "memberRoles")?.some(mr => mr?.itemPerms?.some(p => p.resource === resource && p.actions.includes(action)))
	return isPlainAdmin || (allowPatron && hasPatron) || hasPerm
}


/**
 * Returns EventType documents from backend API and
 * allows listing EventType children at top level
 * 
 * @param {Boolean} flat			If true, includes EventType.children items at array top level
 * @returns {[Object]}				List of EventType documents
 */
export const useEventTypeDocs = (flat) => {
	const { data: data } = useGetEventTypesQuery()
	if (!data) {
		return []
	}
	if (!flat) {
		return data
	}
	const nested = _.flatMap(data, "children")
	return data.concat(nested)
}


/**
 * Returns function that allows retrieving EventType document based on
 * `name` identifier
 * 
 * @returns {Object}											EventType document
 */
export const useGetEventTypeDoc = () => {
	const allDocs = useEventTypeDocs()
	let getEventTypeDoc = (eventTypeName, parentEventName) => {
		return (parentEventName ? (allDocs.find(d => d.name === parentEventName)?.children ?? []) : allDocs).find(d => d.name === eventTypeName)
	}
	return getEventTypeDoc
}

/**
 * Returns function that allows retrieving EventType document title based on
 * `name` identifier
 * 
 * @returns {String}												EventType title
 */
export const useGetEventTypeTitle = () => {
	const getEventTypeDoc = useGetEventTypeDoc()
	return (eventTypeName, parentEventName) => getEventTypeDoc(eventTypeName, parentEventName)?.title ?? ""
}


/**
 * Returns exercise event types inherent to operational class, with the option to
 * also include event types present in 'durationsbytype' response data.
 * 
 * Return values may vary depending on the state of the event type list query.
 * 
 * @param {Object} keyedData 						"durationsbytype" endpoint response's 'data' attribute
 * @returns {Object}										Object with keys `guidedEventTypes`, `ownEventTypes` and query hook return values
 */
export const useGetExerciseEventTypes = (keyedData) => {

	const { data: eventTypeData, ...rest } = useGetEventTypesQuery(null, { skip: !keyedData })

	const opClass = useOperationalClass()
	const opClassGuidedEventTypes = opClass.eventTypes.filter(type => type.isExercise)
	const opClassOwnEventTypes = opClass.personalEventTypes.filter(type => type.isExercise)

	// Event type documents from response data keys
	const eventTypesInResponse = useMemo(() => {
		if (!keyedData || !eventTypeData) {
			return []
		}
		const d = keyedData
		let names = []
		for (let y in d) {
			for (let wk in d[y]) {
				const keys = _.keys(d[y][wk])
				names.push(...keys)
			}
		}
		names = _.uniq(names)
		const eventTypes = eventTypeData.filter(et => names.includes(et.name))
		return eventTypes
	}, [keyedData, eventTypeData])


	// The used event type collection will be union of op class base event types and foreign event types present in stat data response
	const responseGuidedEventTypes = eventTypesInResponse.filter(et => et.classes.some(c => c.name === "group"))
	const guidedEventTypes = _.unionBy(opClassGuidedEventTypes, responseGuidedEventTypes, o => o.name)

	const responseOwnEventTypes = eventTypesInResponse.filter(et => et.classes.some(c => c.name === "personal"))
	const ownEventTypes = _.unionBy(opClassOwnEventTypes, responseOwnEventTypes, o => o.name)

	return { guidedEventTypes, ownEventTypes, ...rest }
}

/** Generic helper hook for managing sorting more easily. 
 * 
 * Works on the premise of a "sort key", that is, any type of valid key for an object e.g. "name".
 * 
 * Initially sort key can be null, this means no sorting is performed and `items` will be returned as the initial input items.
 * 
 * Most important function here is `handleNewSort`; it is used for actually telling this hook to sort according to parameters you define.
 * It works best when used with a list of objects where each object can include appropriate keys (e.g. "sortKey" or "sortMethodInitial").
 * 
 * Optionally you can set the direction initially with `newSortMethodInitial`.
 * 
 * NOTE ON CUSTOM SORTER: `orderBy`'s custom sorter takes in only ONE item, not two.
 * 
*/
export const useTableSorting = (initialItems, initialActiveSortKey = null, initialActiveSortDirection = null) => {
	const DEFAULT_SORT_DIRECTION = "asc"

	const handleNewSort = ({ newSortKey, newSortMethodInitial = null, customSortFn }) => {
		if (newSortKey !== activeSortKey) {
			setActiveSortkey(newSortKey)
		}

		const HAS_NEW_SORT_METHOD = !!newSortMethodInitial
		const NEW_SORT_KEY_IS_EQUAL_TO_CURRENT = newSortKey === activeSortKey

		if (!NEW_SORT_KEY_IS_EQUAL_TO_CURRENT && HAS_NEW_SORT_METHOD) {
			setActiveSortDirection(newSortMethodInitial)
		} else if (NEW_SORT_KEY_IS_EQUAL_TO_CURRENT) {
			setActiveSortDirection(activeSortDirection === "asc" ? "desc" : "asc")
		} else {
			setActiveSortDirection(DEFAULT_SORT_DIRECTION)
		}

		if (customSortFn) {
			setCustomSortFn(() => customSortFn)
		} else {
			setCustomSortFn(null)
		}
	}

	const [activeSortKey, setActiveSortkey] = useState(initialActiveSortKey)
	const [customSortFn, setCustomSortFn] = useState()
	const [activeSortDirection, setActiveSortDirection] = useState(initialActiveSortDirection ?? DEFAULT_SORT_DIRECTION)

	const items = useMemo(() => {
		if (!activeSortKey || !initialItems) {
			return initialItems
		} else {
			// Without using a custom sorter, lodash does the best it can via dynamic comparison based on the field key
			const predicate = customSortFn ? [customSortFn] : activeSortKey
			return _.orderBy(initialItems, predicate, activeSortDirection)
		}
	}, [JSON.stringify(initialItems), activeSortKey, activeSortDirection])

	return { activeSortKey, items, handleNewSort, activeSortDirection }
}


/**
 * Saves state into query parameters. Useful for saving filters between reloads.
 * Usage:
 * 
 * 
 * ```
 * const [state, setState] = useQueryParamState({
 *  	// Convert the query param string into actual state value
 *		getState: (value) => profileIds.find(profile => profile.id == value),
 *		// Convert state into query param string
 *		getParam: (state) => state.id,
 *		// Default value when no value is found from query param. Defaults to `null`
 *		defaultValue: null,
 *		// This is the actual query param key e.g. ?queryParam=value
 *		key: "queryParam"
 *	})
 * 
 * ```
 * 
 * @param {object} options 
 * @param {string} options.key This is the actual query param key e.g. ?queryParam=value
 * @param {function} options.getState Convert the query param string into actual state value
 * @param {function} options.getParam Convert state into query param string
 * @param {any} options.defaultValue Default value when no value is found from query param. Defaults to `null`
 * @returns Array - [state, setState]
 */
export const useQueryParamState = ({
	key,
	getState,
	getParam,
	defaultValue,
	dependOn
}) => {
	const setState  = (newValue) => {
		setActualState(newValue)
		setSearchParams((searchParams) => {
			if (newValue) {
				searchParams.set(key, getParam(newValue))
			} else {
				searchParams.delete(key)
			}
			return searchParams
		  })
	}

	const getStateFromParam = () => {
		const { search } = window.location
		const searchParams = new URLSearchParams(search)
		const param = searchParams.get(key)
		if (!param) return defaultValue ?? null
		const result = getState(param)
		return result
	}

	const [searchParams, setSearchParams] = useSearchParams()
	const [actualState, setActualState] = useState(getStateFromParam)


	useEffect(() => {
		if (!dependOn) return
		setActualState(getStateFromParam())
	}, dependOn ?? [])

	
	return [actualState, setState]
}